import { all, fork, call, put, takeLatest } from 'redux-saga/effects';
import {
  Types as ProductionDetailTypes,
  Creators as ProductionDetailActions,
} from '../ducks/ProductionDetail';
import api from '../../util/Api';
import * as Sentry from '@sentry/react';

function* getProductionDetails({ payload }) {
  try {
    const { status, data } = yield call(api.get, '/production-details', {
      params: payload,
    });
    if (status === 200) {
      yield put(ProductionDetailActions.getProductionDetailsSuccess(data));
    }
    else{
      Sentry.captureMessage('getProductionDetails failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      ProductionDetailActions.getProductionDetailsError('Error listing Production Data')
    );
  }
}

function* postProductionDetail({ payload }) {
  try {
    const { status } = yield call(api.post, '/production-details', payload, {
      headers: {
        ...(payload instanceof FormData
          ? { 'Content-Type': 'multipart/form-data' }
          : {}),
      },
    });
    if (status === 200) {
      yield put(ProductionDetailActions.postProductionDetailSuccess());
    }
    else{
      Sentry.captureMessage('postProductionDetail failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(ProductionDetailActions.postProductionDetailError(error?.response?.data));
  }
}

function* getProductionDetailById({ payload }) {
  const { id, params, edit } = payload;
  try {
    const { status, data } = yield call(api.get, `/production-details/${id}`, {
      params,
    });
    if (status === 200) {
      if (edit && data?.approved !== 'approved') {
        yield put(
          ProductionDetailActions.getProductionDetailByIdError(
            'Production data not found'
          )
        );
      }
      yield put(ProductionDetailActions.getProductionDetailByIdSuccess(data));
    }
    else{
      Sentry.captureMessage('getProductionDetailById failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      ProductionDetailActions.getProductionDetailByIdError('Production Data not found')
    );
  }
}

function* requestAudit({ payload }) {
  try {
    const { status } = yield call(api.get, `/production-details/${payload}/request-audit`);
    if (status === 200) {
      yield put(ProductionDetailActions.getRequestAuditSuccess());
    }
    else{
      Sentry.captureMessage('requestAudit failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      ProductionDetailActions.getRequestAuditError('Error sending email')
    );
  }
}

function* putProductionDetail({ payload }) {
  const { id, data } = payload;
  try {
    const { status } = yield call(api.put, `/production-details/${id}`, data, {
      headers: {
        ...(data instanceof FormData
          ? { 'Content-Type': 'multipart/form-data' }
          : {}),
      },
    });
    if (status === 200) {
      yield put(ProductionDetailActions.putProductionDetailSuccess());
    }
    else{
      Sentry.captureMessage('putProductionDetail failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      ProductionDetailActions.putProductionDetailError('Error editing Production Data')
    );
  }
}

function* deleteProductionDetail({ payload }) {
  try {
    const { status } = yield call(api.delete, `/production-details/${payload}`);
    if (status === 200) {
      yield put(ProductionDetailActions.deleteProductionDetailSuccess());
    }
    else{
      Sentry.captureMessage('deleteProductionDetail failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      ProductionDetailActions.deleteProductionDetailError('Error deleting Production Data')
    );
  }
}

function* approveProductionDetail({ payload }) {
  try {
    const { status } = yield call(api.post, `/production-details/${payload}/approve`);
    if (status === 200) {
      yield put(ProductionDetailActions.approveProductionDetailSuccess());
    }
    else{
      Sentry.captureMessage('approveProductionDetail failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      ProductionDetailActions.approveProductionDetailError(error?.response?.data?.message)
    );
  }
}

function* refuseProductionDetail({ payload }) {
  const { id, data } = payload
  try {
    const { status } = yield call(api.post, `/production-details/${id}/refuse`, data);
    if (status === 200) {
      yield put(ProductionDetailActions.refuseProductionDetailSuccess());
    }
    else{
      Sentry.captureMessage('refuseProductionDetail failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(ProductionDetailActions.refuseProductionDetailError());
  }
}

function* getProductionDetailsWatcher() {
  yield takeLatest(ProductionDetailTypes.GET_PRODUCTION_DETAILS, getProductionDetails);
}

function* postProductionDetailWatcher() {
  yield takeLatest(ProductionDetailTypes.POST_PRODUCTION_DETAIL, postProductionDetail);
}

function* getProductionDetailByIdWatcher() {
  yield takeLatest(ProductionDetailTypes.GET_PRODUCTION_DETAIL_BY_ID, getProductionDetailById);
}

function* requestAuditWatcher() {
  yield takeLatest(ProductionDetailTypes.GET_REQUEST_AUDIT, requestAudit);
}

function* putProductionDetailWatcher() {
  yield takeLatest(ProductionDetailTypes.PUT_PRODUCTION_DETAIL, putProductionDetail);
}

function* deleteProductionDetailWatcher() {
  yield takeLatest(ProductionDetailTypes.DELETE_PRODUCTION_DETAIL, deleteProductionDetail);
}

function* approveProductionDetailWatcher() {
  yield takeLatest(ProductionDetailTypes.APPROVE_PRODUCTION_DETAIL, approveProductionDetail);
}


function* refuseProductionDetailWatcher() {
  yield takeLatest(ProductionDetailTypes.REFUSE_PRODUCTION_DETAIL, refuseProductionDetail);
}

export default function* rootSaga() {
  yield all([
    fork(getProductionDetailsWatcher),
    fork(postProductionDetailWatcher),
    fork(getProductionDetailByIdWatcher),
    fork(requestAuditWatcher),
    fork(putProductionDetailWatcher),
    fork(deleteProductionDetailWatcher),
    fork(approveProductionDetailWatcher),
    fork(refuseProductionDetailWatcher),
  ]);
}
